import axios from "axios";
import store from "../store/index";
import utils from "./utils";

function authHeader() {
  return {
    headers: {
      Authorization: `Bearer ${store?.state?.oidcStore?.access_token}`
    }
  };
}

function hasAccessToken() {
  return !!store?.state?.oidcStore?.access_token;
}

const API = axios.create({
  baseURL: process.env.VUE_APP_API_URL
});

const getBooks = async function() {
  try {
    const response = await API.get("books");
    if (response.status == 200) return response.data.books;
    return [];
  } catch (error) {
    // console.error(error);
    return [];
  }
};

const getUserOverlayInfo = async function() {
  try {
    const response = await API.get("useroverlay", authHeader());
    if (response.status == 200) return response.data.overlayInfos;
    return [];
  } catch (error) {
    // console.error(error);
    return [];
  }
};

const updateUserOverlay = async function(
  bookId,
  lastSectionNumber,
  lastPracticeMode
) {
  const req = { bookId, lastSectionNumber, lastPracticeMode };
  try {
    if (hasAccessToken()) await API.post("useroverlay", req, authHeader());
  } catch {
    // console.error(error);
  }
};

const getSections = async function(bookId, from, to) {
  try {
    const response = await API.get(
      `books/${bookId}/sections?from=${from}&to=${to}`,
      authHeader()
    );
    if (response.status == 200) return response.data.sections;
    return [];
  } catch (error) {
    // console.error(error);
    return [];
  }
};

const submitRecording = async function(
  audioBlob,
  date,
  lengthInSeconds,
  text,
  startTime,
  endTime,
  sectionId
) {
  try {
    let response;
    const form = new FormData();
    form.append("file", audioBlob);
    form.append("date", date);
    form.append("lengthInSeconds", lengthInSeconds);
    form.append("text", text);
    form.append("startTime", startTime);
    form.append("endTime", endTime);
    response = await API.post(`recordings/${sectionId}`, form, authHeader());
    return response.data.recording;
  } catch (error) {
    // console.error(error);
  }
};

const getUserRecordingsForSection = async function(sectionId) {
  try {
    const response = await API.get(`recordings/${sectionId}`, authHeader());
    if (response.status == 200) return response.data.recordings;
    return [];
  } catch (error) {
    // console.error(error);
    return [];
  }
};

const deleteRecording = async function(recordingGuid) {
  try {
    const response = await API.delete(
      `recordings/${recordingGuid}`,
      authHeader()
    );
    return response.status == 200;
  } catch (error) {
    // console.error(error);
    return false;
  }
};

const getAllUsers = async function() {
  try {
    const response = await API.get(`admin/users`, authHeader());
    return response.status == 200 ? response.data.users : [];
  } catch (error) {
    // console.error(error);
    return [];
  }
};

const getAllRecordingsForUser = async function(userId) {
  try {
    const response = await API.get(
      `admin/users/${userId}/recordings`,
      authHeader()
    );
    return response.status == 200 ? response.data.recordings : [];
  } catch (error) {
    // console.error(error);
    return [];
  }
};

const getGlobalStats = async function() {
  try {
    const response = await API.get(`admin/globalstats`, authHeader());
    return response.status == 200 ? response.data : {};
  } catch (error) {
    // console.error(error);
    return {};
  }
};

const getTarget = async function(dateString) {
  try {
    const response = await API.get(`target/${dateString}`, authHeader());
    if (response.status == 200) return response.data.target;
    return {};
  } catch (error) {
    // console.error(error);
    return {};
  }
};

const updateTarget = async function(newTarget) {
  const localISOString = utils.toLocalISOString(new Date());
  const req = {
    targetDailyRecordings: newTarget.targetDailyRecordings,
    targetDailySeconds: newTarget.targetDailySeconds,
    targetDailySpellings: newTarget.targetDailySpellings,
    clientToday: localISOString
  };
  try {
    if (hasAccessToken()) await API.post("target", req, authHeader());
  } catch {
    // console.error(error);
  }
};

const cleanAnonymousRecordings = async function(lengthLow, lengthHigh) {
  const req = { lengthLow, lengthHigh };
  try {
    if (hasAccessToken())
      await API.post("admin/recordings/clean", req, authHeader());
  } catch {
    // console.error(error);
  }
};

const getHomePageStats = async function() {
  try {
    const response = await API.get("home");
    if (response.status == 200) return response.data;
    return [];
  } catch (error) {
    // console.error(error);
    return [];
  }
};

const getSettings = async function() {
  try {
    const response = await API.get(`settings`, authHeader());
    if (response.status == 200) return response.data;
    return {};
  } catch (error) {
    // console.error(error);
    return {};
  }
};

const saveSettings = async function(settings) {
  try {
    if (hasAccessToken()) await API.post("settings", settings, authHeader());
  } catch {
    // console.error(error);
  }
};

const recordSpelling = async function(bookId, correct) {
  try {
    const clientDate = utils.toLocalISOString(new Date());
    const req = { bookId, correct, clientDate };
    if (hasAccessToken()) await API.post("spellings", req, authHeader());
  } catch {
    // console.error(error);
  }
};

const getProgress = async function() {
  try {
    const response = await API.get(`progress`, authHeader());
    if (response.status == 200) return response.data;
    return {};
  } catch (error) {
    return {};
  }
};

const getStudentProgress = async function(studentGuid) {
  try {
    const response = await API.get(`progress/${studentGuid}`, authHeader());
    if (response.status == 200) return response.data;
    return {};
  } catch (error) {
    return {};
  }
};

const createStripeSession = async function(
  seats,
  months,
  licenceGuid,
  stripeSuccessUrl,
  stripeCancelUrl
) {
  try {
    const req = {
      stripeSuccessUrl,
      stripeCancelUrl,
      seats,
      months,
      licenceGuid
    };
    if (!hasAccessToken()) return;
    const response = await API.post("purchase", req, authHeader());
    if (response.status < 300) return response.data;
  } catch {
    //
  }
};

const getLicences = async function() {
  try {
    const response = await API.get("licences", authHeader());
    if (response.status == 200) return response.data;
    return [];
  } catch (error) {
    // console.error(error);
    return [];
  }
};

const getLicence = async function(licenceGuid) {
  try {
    const response = await API.get(`licences/${licenceGuid}`, authHeader());
    if (response.status == 200) return response.data;
    return undefined;
  } catch (error) {
    return undefined;
  }
};

const getLicenceCodes = async function() {
  try {
    const response = await API.get("licences/codes", authHeader());
    if (response.status == 200) return response.data;
    return [];
  } catch (error) {
    // console.error(error);
    return [];
  }
};

const activateLicenceCode = async function(licenceCode) {
  try {
    if (!hasAccessToken()) return;
    const response = await API.post(
      `licences/codes/${licenceCode}`,
      {},
      authHeader()
    );
    return response.status < 300;
  } catch {
    return false;
  }
};

const deactivateLicenceCode = async function(licenceCode) {
  try {
    if (!hasAccessToken()) return;
    const response = await API.delete(
      `licences/codes/${licenceCode}`,
      authHeader()
    );
    return response.status < 300;
  } catch {
    return false;
  }
};

const createLicenceAsAdmin = async function(ownerEmail, seats, months) {
  const req = { ownerEmail, seats, months };
  try {
    if (hasAccessToken()) await API.post("admin/licences", req, authHeader());
  } catch {
    // console.error(error);
  }
};

const getTutorCode = async function() {
  try {
    const response = await API.get("students/tutorcode", authHeader());
    if (response.status == 200) return response.data;
    return undefined;
  } catch (error) {
    return undefined;
  }
};

const connectToTutor = async function(tutorCode) {
  try {
    if (!hasAccessToken()) return;
    const response = await API.post(`tutors/${tutorCode}`, {}, authHeader());
    return response.status < 300;
  } catch {
    return false;
  }
};

const getTutors = async function() {
  try {
    const response = await API.get("tutors", authHeader());
    if (response.status == 200) return response.data;
    return [];
  } catch (error) {
    return [];
  }
};

const disconnectFromTutor = async function(tutorCode) {
  try {
    if (!hasAccessToken()) return;
    const response = await API.delete(`tutors/${tutorCode}`, authHeader());
    return response.status < 300;
  } catch {
    return false;
  }
};

const getStudents = async function() {
  try {
    const response = await API.get("students", authHeader());
    if (response.status == 200) return response.data;
    return [];
  } catch (error) {
    return [];
  }
};

const disconnectStudent = async function(studentGuid) {
  try {
    if (!hasAccessToken()) return;
    const response = await API.delete(`students/${studentGuid}`, authHeader());
    return response.status < 300;
  } catch {
    return false;
  }
};

const shareRecordingWithTutor = async function(recordingGuid, tutorGuid) {
  try {
    if (!hasAccessToken()) return;
    const response = await API.post(
      `recordings/${recordingGuid}/share/${tutorGuid}`,
      {},
      authHeader()
    );
    return response.status < 300 ? response.data : undefined;
  } catch {
    return undefined;
  }
};

const getStudentRecordings = async function(studentGuid) {
  try {
    const response = await API.get(
      `students/${studentGuid}/recordings`,
      authHeader()
    );
    if (response.status == 200) return response.data;
    return [];
  } catch (error) {
    // console.error(error);
    return [];
  }
};

const praiseStudentRecording = async function(recordingGuid) {
  try {
    if (!hasAccessToken()) return;
    const response = await API.post(
      `recordings/${recordingGuid}/praise`,
      {},
      authHeader()
    );
    return response.status < 300;
  } catch {
    return false;
  }
};

const submitFeedbackForStudentRecording = async function(
  recordingGuid,
  feedback
) {
  try {
    if (!hasAccessToken()) return;
    const response = await API.post(
      `recordings/${recordingGuid}/feedback`,
      { feedback },
      authHeader()
    );
    return response.status < 300;
  } catch {
    return false;
  }
};

const getNotifications = async function() {
  try {
    const response = await API.get("notifications", authHeader());
    if (response.status == 200) return response.data;
    return [];
  } catch (error) {
    // console.error(error);
    return [];
  }
};

const dismissNotification = async function(notificationGuid) {
  try {
    if (!hasAccessToken()) return;
    const response = await API.delete(
      `notifications/${notificationGuid}`,
      authHeader()
    );
    return response.status < 300;
  } catch {
    return false;
  }
};

export default {
  getBooks,
  getUserOverlayInfo,
  updateUserOverlay,
  getSections,
  submitRecording,
  getUserRecordingsForSection,
  deleteRecording,
  getAllUsers,
  getAllRecordingsForUser,
  getGlobalStats,
  getTarget,
  updateTarget,
  cleanAnonymousRecordings,
  getHomePageStats,
  getSettings,
  saveSettings,
  recordSpelling,
  getProgress,
  getStudentProgress,
  createStripeSession,
  getLicences,
  getLicence,
  getLicenceCodes,
  activateLicenceCode,
  deactivateLicenceCode,
  createLicenceAsAdmin,
  getTutorCode,
  connectToTutor,
  getTutors,
  disconnectFromTutor,
  getStudents,
  disconnectStudent,
  shareRecordingWithTutor,
  getStudentRecordings,
  praiseStudentRecording,
  submitFeedbackForStudentRecording,
  getNotifications,
  dismissNotification
};
